import { Cart, useStoreApi } from '@givebacks/api/store';
import { useQuery } from '@tanstack/react-query';

import { storeKeys } from '../../query-key-factory';

type GetCartParams<T = Cart> = {
  cartId?: string;
  select?: (data: Cart) => T;
  causeId?: string;
  pendingCredential?: string;
};
const useGetCart = <T = Cart>({
  cartId,
  causeId,
  pendingCredential,
}: Omit<GetCartParams<T>, 'select'>) => {
  const StoreApi = useStoreApi();
  return async (): Promise<Cart> => {
    const causeParam = causeId ? `cause_id=${causeId}` : undefined;
    const pendingCredentialParam = pendingCredential
      ? `pending_card_type=${pendingCredential}`
      : undefined;
    const paramString = [causeParam, pendingCredentialParam].filter(Boolean).join('&');
    const { data } = await StoreApi.get(
      `/carts/${cartId}?${paramString}&include=fee_groups,summary`
    );
    return data.cart;
  };
};

/**
 * @deprecated use useShowCart from @givebacks/api/store instead
 */
export function useShowCart<T = Cart>({
  cartId,
  select,
  causeId,
  pendingCredential,
}: GetCartParams<T>) {
  const getCart = useGetCart({ cartId, causeId, pendingCredential });
  const storeKeyString = storeKeys.id(causeId ?? 'user').carts.id(cartId ?? '').detail;
  const key = [...storeKeyString] as unknown[];

  pendingCredential && key.push({ pendingCredential: pendingCredential });
  return useQuery({
    queryKey: key,
    queryFn: getCart,
    enabled: !!cartId,
    select,
  });
}

export const useShowCartItem = (cartId?: string) => {
  return useShowCart({ cartId, select: (data) => data?.cart_items[0] });
};

export const useShowCartItemStoreItem = (cartId?: string) => {
  return useShowCart({ cartId, select: (data) => data?.cart_items[0].store_item });
};

export const useShowPendingCartFees = (cartId?: string, pendingCredential?: string) => {
  return useShowCart({ cartId, pendingCredential });
};
